import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  MapPinIcon,
  UsersIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  SparklesIcon,
  ClockIcon,
  HomeIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

interface Package {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  priceUSD: number;
  priceAUD: number;
  validityPeriod: number;
  maxGuests: number;
  nights: number;
  highlights?: Array<string | { title: string; imageUrl?: string }>;
  bannerImage?: string;
  isActive: boolean;
  isLive: boolean;
  lastMinuteDealPriceUSD?: number;
  lastMinuteDealPriceAUD?: number;
  lastMinuteDealExpiryDate?: string;
  hasActiveLastMinuteDeal?: boolean;
}

interface Destination {
  _id: string;
  name: string;
  slug: string;
  image: string;
  description?: string;
  packages: Package[];
}

const DestinationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatPrice } = useCurrency();
  const [destination, setDestination] = useState<Destination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState(false);

  // Helper to get effective AUD price for sorting
  const getEffectiveAUDPrice = (pkg: Package) => {
    const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
    const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
    const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
    
    if (hasDealPrice && isDealActive && dealPriceAUD) {
      return dealPriceAUD;
    }
    return pkg.priceAUD;
  };

  useEffect(() => {
    const fetchDestination = async () => {
      if (!id) return;

      try {
        console.log('Fetching destination with ID:', id);
        
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/destinations/${id}`
        );
        
        console.log('API Response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Destination data received:', data);
          setDestination(data.destination);
        } else {
          const errorText = await response.text();
          console.error('API Error:', response.status, errorText);
          setError(`Failed to load destination: ${response.status}`);
        }
      } catch (error) {
        console.error('Full error object:', error);
        setError(`Failed to load destination data: ${error instanceof Error ? error.message : 'Network error'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDestination();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-secondary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading destination details...</p>
        </div>
      </div>
    );
  }

  if (!destination) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Destination Not Found</h1>
          {error && <p className="text-red-600 mb-4">{error}</p>}
          <p className="text-gray-600 mb-6">The destination you're looking for could not be found.</p>
          <Link to="/destinations" className="text-secondary-600 hover:text-secondary-700 bg-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all">
            ← Back to Destinations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{destination.name} - Explore Bali | Bali Getaway</title>
        <meta name="description" content={destination.description || `Discover ${destination.name} with premium packages and resorts`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Modern Hero Section */}
        <div className="relative min-h-[500px] flex items-center overflow-hidden">
          {/* Background Image with Gradient Overlay */}
          <div className="absolute inset-0">
            {!imageError && destination.image ? (
              <img
                src={destination.image}
                alt={destination.name}
                className="w-full h-full object-cover"
                onError={() => {
                  console.error('❌ Image failed to load:', destination.image);
                  setImageError(true);
                }}
                onLoad={() => console.log('✅ Image loaded successfully:', destination.image)}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-orange-400 via-orange-500 to-yellow-500" />
            )}
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          </div>

          {/* Hero Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
            {/* Breadcrumb */}
            <Link 
              to="/" 
              className="inline-flex items-center text-white/90 hover:text-white mb-8 transition-colors group"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              <span>Back to Home</span>
            </Link>

            {/* Destination Title & Description */}
            <div className="max-w-3xl">
              <div className="inline-flex items-center space-x-2 mb-4">
                <MapPinIcon className="h-6 w-6 text-orange-400" />
                <span className="text-orange-400 font-semibold uppercase tracking-wide text-sm">
                  Bali Destination
                </span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight">
                {destination.name}
              </h1>
              
              {destination.description && (
                <p className="text-xl md:text-2xl text-white/90 leading-relaxed mb-8">
                  {destination.description}
                </p>
              )}

              {/* Stats */}
              <div className="flex flex-wrap gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4 border border-white/20">
                  <div className="text-3xl font-bold text-white">
                    {destination.packages.filter(p => p.isActive && p.isLive).length}
                  </div>
                  <div className="text-white/80 text-sm">Available Packages</div>
                </div>
              </div>
            </div>
          </div>

          {/* Decorative Element */}
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50 to-transparent" />
        </div>

        {/* Packages Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {destination.packages && destination.packages.length > 0 ? (
            <section>
              {/* Section Header */}
              <div className="text-center mb-16">
                <div className="inline-flex items-center space-x-2 mb-4">
                  <SparklesIcon className="h-6 w-6 text-orange-500" />
                  <span className="text-orange-600 font-semibold uppercase tracking-wide text-sm">
                    Curated Experiences
                  </span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
                  Featured <span className="bg-gradient-to-r from-orange-600 to-yellow-500 bg-clip-text text-transparent">Packages</span>
                </h2>
                
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Discover {destination.packages.filter(pkg => pkg.isActive && pkg.isLive).length} handpicked packages designed to give you the ultimate {destination.name} experience
                </p>
              </div>
              
              {/* Package Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {destination.packages
                  .filter(pkg => pkg.isActive && pkg.isLive)
                  .sort((a, b) => getEffectiveAUDPrice(a) - getEffectiveAUDPrice(b)) // Sort by effective AUD price
                  .map((pkg) => {
                  const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
                  const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
                  const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
                  const hasLastMinuteDeal = hasDealPrice && isDealActive;
                  const displayPriceAUD = hasLastMinuteDeal && dealPriceAUD ? dealPriceAUD : pkg.priceAUD;
                  
                  return (
                    <div key={pkg._id} className="group relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100">
                      {/* Last Minute Deal Badge */}
                      {hasLastMinuteDeal && (
                        <div className="absolute top-4 left-4 z-20 bg-gradient-to-r from-red-500 to-pink-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg animate-pulse">
                          ⚡ Last Minute Deal!
                        </div>
                      )}

                      {/* Package Image */}
                      <div className="relative h-64 overflow-hidden bg-gradient-to-br from-orange-100 to-yellow-100">
                        {pkg.bannerImage ? (
                          <>
                            <img
                              src={pkg.bannerImage}
                              alt={pkg.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <MapPinIcon className="h-20 w-20 text-orange-300" />
                          </div>
                        )}
                        
                        {/* Floating Info Badge */}
                        <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg">
                          <div className="flex items-center space-x-2">
                            <ClockIcon className="h-4 w-4 text-orange-500" />
                            <span className="text-sm font-semibold text-gray-800">{pkg.validityPeriod} months</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Card Content */}
                      <div className="p-6">
                        {/* Package Name */}
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors line-clamp-2">
                          {pkg.name}
                        </h3>

                        {/* Package Description */}
                        {pkg.description && (
                          <p className="text-gray-600 mb-4 line-clamp-3 text-sm leading-relaxed">
                            {pkg.description}
                          </p>
                        )}

                        {/* Package Details */}
                        <div className="flex items-center space-x-4 mb-6">
                          <div className="flex items-center text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
                            <UsersIcon className="h-4 w-4 mr-2 text-orange-500" />
                            <span className="font-medium">{pkg.maxGuests} guests</span>
                          </div>
                          {pkg.nights && (
                            <div className="flex items-center text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
                              <HomeIcon className="h-4 w-4 mr-2 text-orange-500" />
                              <span className="font-medium">{pkg.nights} nights</span>
                            </div>
                          )}
                        </div>

                        {/* Highlights Preview */}
                        {pkg.highlights && pkg.highlights.length > 0 && (
                          <div className="mb-6 space-y-2">
                            {pkg.highlights.slice(0, 3).map((highlight, idx) => {
                              // Handle both string and object formats
                              const text = typeof highlight === 'string' ? highlight : highlight.title;
                              return (
                                <div key={idx} className="flex items-start text-sm text-gray-600">
                                  <CheckCircleIcon className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                                  <span className="line-clamp-1">{text}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Price Section */}
                        <div className="border-t border-gray-100 pt-4">
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-sm text-gray-500 mb-1">From</p>
                              <div className="flex items-baseline space-x-2">
                                {hasLastMinuteDeal && (
                                  <span className="text-lg text-gray-400 line-through">
                                    A${pkg.priceAUD.toLocaleString()}
                                  </span>
                                )}
                                <span className={`text-3xl font-extrabold ${hasLastMinuteDeal ? 'text-red-600' : 'bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent'}`}>
                                  A${displayPriceAUD.toLocaleString()}
                                </span>
                              </div>
                            </div>
                            
                            <Link
                              to={`/packages/${pkg._id}`}
                              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-full font-bold hover:from-orange-600 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 group-hover:shadow-orange-200"
                            >
                              <span>View</span>
                              <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : (
            <div className="text-center py-24 bg-gradient-to-br from-gray-50 to-orange-50 rounded-3xl border-2 border-dashed border-orange-200">
              <div className="bg-gradient-to-br from-orange-100 to-yellow-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPinIcon className="h-12 w-12 text-orange-500" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-3">Exciting Packages Coming Soon!</h3>
              <p className="text-lg text-gray-600 mb-8 max-w-lg mx-auto">
                We're curating amazing experiences for {destination.name}. Check back soon for exclusive offers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/packages"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-full font-bold hover:from-orange-600 hover:to-yellow-600 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
                >
                  Browse All Packages
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center px-8 py-4 bg-white text-orange-600 border-2 border-orange-600 rounded-full font-bold hover:bg-orange-50 transition-all duration-300"
                >
                  <ArrowLeftIcon className="mr-2 h-5 w-5" />
                  Back to Home
                </Link>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default DestinationDetailPage;
